import type { NextPageWithLayout } from 'types/page';
import {
  DEFAULT_SIDEBAR_LAYOUT_PROPS,
  withSidebarLayout,
} from 'layouts/SidebarPage/withSidebarLayout';
import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'components/Head';
import CalendarTabsHeader from 'screens/Calendar/components/CalendarTabsHeader';
import { slugToComponentMap, CalendarSlug } from 'screens/Calendar/lib/maps';
import { calendarTabs } from 'constants/calendarTabs';
import {
  getUnifiedEarningsCalendar,
  getUnifiedDividendsCalendar,
  getUnifiedSplitsCalendar,
} from 'services/server/stockApi/lib';
import { isValidUSStockSymbol } from 'utils/shared/tickers/validation';

const SYMBOL_BASED_SLUGS: CalendarSlug[] = ['earnings', 'dividends', 'splits'];
const REVALIDATE_TIME_SECONDS = 60 * 60 * 8;
const ERROR_REVALIDATE_TIME_SECONDS = 60 * 5; // 5 minutes

interface PageProps {
  slug: CalendarSlug;
  data: any[];
  symbol: string;
}

export default function CalendarSymbolPage({ slug, symbol, data }: PageProps) {
  const tab = calendarTabs.find((t) => t.slug === slug);
  const PageComponent = slugToComponentMap[slug];

  if (!PageComponent || !tab) return <div>Not Found</div>;

  const pageTitle = `${tab.name} Calendar${symbol ? ` for ${symbol}` : ''}`;

  return (
    <>
      <Head
        title={pageTitle}
        description={
          symbol
            ? `${tab.name} calendar for ${symbol}`
            : `${tab.name} calendar with latest updates for ticker`
        }
      />
      <>
        <CalendarTabsHeader title={pageTitle} activeSlug={slug} />
        <PageComponent data={data} symbol={symbol} slug={slug} />
      </>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const slug = params?.slug as CalendarSlug;
  const symbol = (params?.symbol as string)?.toUpperCase();

  if (!SYMBOL_BASED_SLUGS.includes(slug)) {
    return { notFound: true };
  }

  if (!isValidUSStockSymbol(symbol)) {
    return { notFound: true };
  }

  switch (slug) {
    case 'earnings': {
      const earningsData = await getUnifiedEarningsCalendar(symbol);

      const filteredData = earningsData.dates
        .map((dateEntry) => {
          const filteredItems = dateEntry.items.filter((item) => item.t?.toUpperCase() === symbol);
          if (filteredItems.length > 0) {
            return {
              ...dateEntry,
              items: filteredItems,
            };
          }
          return null;
        })
        .filter(Boolean);
      return {
        props: { slug, data: filteredData, symbol },
        revalidate: process.env.TARGET_PLATFORM === 'MOBILE' ? undefined : REVALIDATE_TIME_SECONDS,
      };
    }

    case 'dividends': {
      const dividendsData = await getUnifiedDividendsCalendar(symbol);

      const filteredData = dividendsData.dates
        .map((dateEntry) => {
          const filteredItems = dateEntry.items.filter((item) => item.t?.toUpperCase() === symbol);
          if (filteredItems.length > 0) {
            return {
              ...dateEntry,
              items: filteredItems,
            };
          }
          return null;
        })
        .filter(Boolean);
      return {
        props: { slug, data: filteredData, symbol },
        revalidate: process.env.TARGET_PLATFORM === 'MOBILE' ? undefined : REVALIDATE_TIME_SECONDS,
      };
    }

    case 'splits': {
      const splitsData = await getUnifiedSplitsCalendar(symbol);

      const filteredData = splitsData.dates
        .map((dateEntry) => {
          const filteredItems = dateEntry.items.filter((item) => item.t?.toUpperCase() === symbol);
          if (filteredItems.length > 0) {
            return {
              ...dateEntry,
              items: filteredItems,
            };
          }
          return null;
        })
        .filter(Boolean);
      return {
        props: { slug, data: filteredData, symbol },
        revalidate: process.env.TARGET_PLATFORM === 'MOBILE' ? undefined : REVALIDATE_TIME_SECONDS,
      };
    }

    default:
      return {
        notFound: true,
        revalidate:
          process.env.TARGET_PLATFORM === 'MOBILE' ? undefined : ERROR_REVALIDATE_TIME_SECONDS,
      };
  }
};
(CalendarSymbolPage as NextPageWithLayout<PageProps>).getLayout = withSidebarLayout(
  DEFAULT_SIDEBAR_LAYOUT_PROPS,
);
